// GlobalPointsContext.js - React Query 기반 전역 포인트 상태 관리 (이전 시스템 로직 복원)
import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import pointsService from '../services/pointsService';
import { useUser } from './UserContext';
import useRouteChangeSync from '../hooks/useRouteChangeSync';

// 전역 포인트 Context
const GlobalPointsContext = createContext(null);

// 🚀 React Query 기반 GlobalPointsProvider (이전 시스템 로직 복원)
export const GlobalPointsProvider = ({ children }) => {
  const { tokenUserID } = useUser();
  const queryClient = useQueryClient();
  const prevTokenUserIDRef = useRef(null);
  
  // 🚨 사용자 전환 감지 및 캐시 초기화
  useEffect(() => {
    if (tokenUserID && tokenUserID !== prevTokenUserIDRef.current) {
      // 이전 사용자의 모든 쿼리 캐시 제거
      if (prevTokenUserIDRef.current) {
        queryClient.removeQueries(['points', prevTokenUserIDRef.current]);
        queryClient.removeQueries(['timer', prevTokenUserIDRef.current]);
      }
      
      prevTokenUserIDRef.current = tokenUserID;
    }
  }, [tokenUserID, queryClient]);

  // 🎯 포인트 조회 (React Query가 자동으로 캐싱, 동기화, 재시도 처리)
  // 🚀 API 병렬화: tokenUserID를 받자마자 즉시 API 호출 (UserContext, GlobalTimer와 병렬 실행)
  const {
    data: points = 0,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['points', tokenUserID],
    queryFn: async () => {
      if (!tokenUserID) return 0;
      return await pointsService.getPoints();
    },
    enabled: !!tokenUserID, // tokenUserID가 있을 때만 실행 (즉시 실행되어 병렬화)
    staleTime: 30000,       // 30초 캐시
    refetchOnWindowFocus: true, // 백그라운드 복귀 시 자동 갱신 (이전 시스템과 동일)
    refetchOnReconnect: true,   // 네트워크 재연결 시 자동 갱신
    onSuccess: (serverPoints) => {
      // 포인트 업데이트 이벤트 발생 (기존 코드 호환성)
      window.dispatchEvent(new CustomEvent('globalPointsUpdated', {
        detail: { points: serverPoints, timestamp: Date.now() }
      }));
    },
    onError: (error) => {
      console.error('❌ 포인트 조회 실패:', error);
    }
  });

  // 🚀 React Router 라우트 변경 시 자동 포인트 동기화 (이전 시스템과 동일)
  const syncPointsFromServer = useCallback(async () => {
    if (!tokenUserID) return;
    try {
      queryClient.invalidateQueries(['points', tokenUserID]);
      await refetch();
    } catch (error) {
      console.error('❌ 포인트 동기화 실패:', error);
    }
  }, [tokenUserID, refetch, queryClient]);

  useRouteChangeSync(syncPointsFromServer, tokenUserID);

  // 🎯 포인트 추가 (즉시 서버 전송 보장)
  const addPointsMutation = useMutation({
    mutationFn: async ({ amount, source, metadata }) => {
      // 🚀 1단계: 큐에 추가
      const entryId = pointsService.addPoints(amount, source, metadata, true); // skipLocalUpdate: true
      if (!entryId) {
        throw new Error('포인트 큐 추가 실패');
      }
      
      // 🚀 2단계: 즉시 서버로 전송 보장 (백그라운드 처리 대신 즉시 플러시)
      const pointsManager = (await import('../services/pointsManager')).default;
      const queueStatus = pointsManager.getQueueStatus();
      
      // 🚀 큐에 포인트가 있으면 즉시 플러시 (중복 방지: 큐가 비어있으면 스킵)
      if (queueStatus.queueLength > 0 || queueStatus.totalPendingPoints > 0) {
        // 🚀 플러시 실패 시 에러를 던져서 모달이 닫히지 않도록 함
        await pointsManager.forceFlush();
        // 🚀 최적화: 0.5초 대기 제거 - 서버 응답을 기다리지 않고 즉시 조회
        // 서버가 아직 처리 중이면 다음 폴링에서 자동으로 동기화됨
      }
      
      // 🚀 3단계: 서버에서 최신 포인트 조회 (실패 시 에러 던짐)
      // 서버 조회 실패 시에도 모달이 닫히지 않도록 함
      const serverPoints = await pointsService.getPoints();
      
      return { success: true, entryId, serverPoints, flushed: true };
    },
    onMutate: async ({ amount }) => {
      // 1. 기존 쿼리 취소 (충돌 방지)
      await queryClient.cancelQueries(['points', tokenUserID]);
      
      // 2. 이전 값 백업 (복구용)
      const previousPoints = queryClient.getQueryData(['points', tokenUserID]);

      // 3. 낙관적 업데이트 (즉시 UI 반영) - 이전 시스템과 동일
      queryClient.setQueryData(['points', tokenUserID], (old) => (old || 0) + amount);
      
      // 4. 백업 데이터 반환 (에러 시 복구용)
      return { previousPoints };
    },
    onError: (error, variables, context) => {
      // 큐 추가 실패한 경우에만 롤백
      if (error.message.includes('큐 추가 실패')) {
        console.error('❌ 포인트 큐 추가 실패:', error);
        if (context?.previousPoints !== undefined) {
          queryClient.setQueryData(['points', tokenUserID], context.previousPoints);
            }
        
        // 사용자에게 에러 알림
        window.dispatchEvent(new CustomEvent('pointsError', {
          detail: { 
            type: 'add',
            error: error.message,
            amount: variables.amount,
            critical: true
          }
        }));
      } else {
        // 기타 에러는 큐에 추가되어 있으므로 경고만 표시
        console.warn('⚠️ [GlobalPoints] 포인트 추가 중 예상치 못한 에러:', error);
      }
    },
    onSuccess: (data) => {
      // 🚀 서버 포인트로 캐시 업데이트 (정확한 값 반영)
      if (data?.serverPoints !== null && data?.serverPoints !== undefined) {
        queryClient.setQueryData(['points', tokenUserID], data.serverPoints);
      }
      
      // 포인트 추가 성공 이벤트 발생 (이전 시스템과 동일)
      const currentPoints = queryClient.getQueryData(['points', tokenUserID]) || 0;
      window.dispatchEvent(new CustomEvent('pointsAdded', {
        detail: { 
          points: currentPoints,
          timestamp: Date.now() 
        }
      }));
        
      // 포인트 업데이트 이벤트 발생 (기존 코드 호환성)
      window.dispatchEvent(new CustomEvent('globalPointsUpdated', {
        detail: { 
          points: currentPoints,
          timestamp: Date.now() 
        }
      }));
    }
  });

  // 🎯 포인트 차감 (이전 시스템과 동일)
  const subtractPointsMutation = useMutation({
    mutationFn: async ({ amount, reason }) => {
      return await pointsService.subtractPoints(amount, reason);
    },
    onMutate: async ({ amount }) => {
      await queryClient.cancelQueries(['points', tokenUserID]);
      const previousPoints = queryClient.getQueryData(['points', tokenUserID]);
      queryClient.setQueryData(['points', tokenUserID], (old) => Math.max((old || 0) - amount, 0));
      return { previousPoints };
    },
    onError: (error, variables, context) => {
      console.error('❌ 포인트 차감 실패:', error);
      if (context?.previousPoints !== undefined) {
        queryClient.setQueryData(['points', tokenUserID], context.previousPoints);
      }
      
      window.dispatchEvent(new CustomEvent('pointsError', {
        detail: { 
          type: 'subtract',
          error: error.message,
          amount: variables.amount
        }
      }));
    },
    onSuccess: (result, variables) => {
      // 서버 응답으로 정확한 포인트 업데이트
      if (result?.success && result?.newPoints !== undefined) {
        queryClient.setQueryData(['points', tokenUserID], result.newPoints);
      } else {
        // 서버 응답이 없으면 캐시 무효화 후 재조회
        queryClient.invalidateQueries(['points', tokenUserID]);
    }
    
      // 교환 완료 이벤트 발생 (이전 시스템과 동일)
      window.dispatchEvent(new CustomEvent('pointsExchangeCompleted', {
        detail: { 
          amount: variables.amount,
          timestamp: Date.now()
        }
      }));
    }
  });

  // 🎯 강제 동기화 (페이지 진입 시) - 최적화: refetchQueries 사용
  const forceSync = useCallback(async () => {
    if (!tokenUserID) return;
    
    try {
      // 🚀 최적화: 큐 상태 확인
      const pointsManager = (await import('../services/pointsManager')).default;
      const queueStatus = pointsManager.getQueueStatus();
      
      // 1. 큐에 포인트가 있으면 먼저 처리
      if (queueStatus.queueLength > 0 || queueStatus.totalPendingPoints > 0) {
        await pointsManager.forceFlush();
        // 🚀 최적화: 0.5초 대기 제거 - 서버 응답을 기다리지 않고 즉시 조회
        // 서버가 아직 처리 중이면 다음 폴링에서 자동으로 동기화됨
      }
      
      // 2. 🚀 캐시 무시하고 서버에서 fresh 데이터 가져오기 (staleTime 무시)
      // invalidateQueries + refetch는 staleTime 때문에 작동하지 않을 수 있으므로
      // refetchQueries를 직접 사용하여 강제로 refetch
      await queryClient.refetchQueries({
        queryKey: ['points', tokenUserID],
        type: 'active',
        exact: true,
      });
    } catch (error) {
      console.error('❌ [GlobalPoints] 강제 동기화 실패:', error);
      // 에러가 나도 재조회 시도
      try {
        await queryClient.refetchQueries({
          queryKey: ['points', tokenUserID],
          type: 'active',
          exact: true,
        });
      } catch (retryError) {
        console.error('❌ [GlobalPoints] 재시도 실패:', retryError);
      }
    }
  }, [tokenUserID, queryClient]);

  // 🎯 사용하기 쉬운 API 제공 (이전 시스템과 동일)
  const addPoints = useCallback(
    (amount, source = 'unknown', metadata = {}) => {
      return addPointsMutation.mutateAsync({ amount, source, metadata });
    },
    [addPointsMutation]
  );

  const subtractPoints = useCallback(
    (amount, reason = 'unknown') => {
      return subtractPointsMutation.mutateAsync({ amount, reason });
    },
    [subtractPointsMutation]
  );

  // 🎯 Context value 메모이제이션 (불필요한 리렌더링 방지)
  const value = React.useMemo(() => ({
    // 포인트 데이터
    points,
    isLoading,
    error,
    
    // 액션
    addPoints,
    subtractPoints,
    forceSync,
    
    // 상태 (기존 코드 호환성)
    lastSyncTime: Date.now(), // 매 렌더링마다 변경되지만 실제로 사용되지 않는 값
    tokenUserID,
    
    // 추가 유틸리티
    refetch,
    syncPointsFromServer // 이전 시스템과 동일한 API
  }), [points, isLoading, error, addPoints, subtractPoints, forceSync, tokenUserID, refetch, syncPointsFromServer]);

  return (
    <GlobalPointsContext.Provider value={value}>
      {children}
    </GlobalPointsContext.Provider>
  );
};

// 🎯 Custom Hook
export const useGlobalPoints = () => {
  const context = useContext(GlobalPointsContext);
  if (!context) {
    throw new Error('useGlobalPoints must be used within a GlobalPointsProvider');
  }
  return context;
};

export default GlobalPointsContext;